import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublishIcon from '@mui/icons-material/Publish';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import TemplatePreview from './TemplatePreview';
import {
  listTemplates,
  createTemplate as createFormsTemplate,
  updateTemplate as updateFormsTemplate
} from '../../services/formsApi';

const statusColorMap = {
  draft: 'default',
  published: 'success',
  archived: 'default'
};

const TemplatePanel = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    template_type: 'builder'
  });

  const navigate = useNavigate();

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTemplates();
      setTemplates(data);
      if (data.length > 0 && !selectedTemplateId) {
        setSelectedTemplateId(data[0].id);
      }
    } catch (err) {
      setError(err.message || 'Unable to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = () => {
    setFormState({ name: '', description: '', template_type: 'builder' });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleCreateTemplate = async (event) => {
    event.preventDefault();
    if (!formState.name.trim()) {
      setError('Template name is required');
      return;
    }

    setSaving(true);
    setError(null);

    const schema = {
      status: 'draft',
      template_type: formState.template_type,
      description: formState.description.trim(),
      fields: [
        {
          id: `field-${Date.now()}-ack`,
          field_key: 'acknowledgement',
          field_type: 'checkbox',
          label: 'I acknowledge the information above',
          placeholder: '',
          required: true,
          sort_order: 0,
          config: {
            position: {
              page: 1,
              x: 50,
              y: 50
            }
          }
        },
        {
          id: `field-${Date.now()}-sig`,
          field_key: 'signature',
          field_type: 'signature',
          label: 'Signature',
          required: true,
          sort_order: 1,
          config: {
            position: {
              page: 1,
              x: 50,
              y: 120
            }
          }
        }
      ],
      attachments: []
    };

    try {
      await createFormsTemplate({
        name: formState.name.trim(),
        schema,
        tags: [],
        cycle: null
      });
      setDialogOpen(false);
      await loadTemplates();
    } catch (err) {
      console.error('Create template failed', err);
      setError(err.message || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishTemplate = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    setError(null);
    try {
      await updateFormsTemplate(selectedTemplate.id, {
        schema: {
          ...(selectedTemplate.schema || {}),
          status: 'published'
        }
      });
      await loadTemplates();
      setSelectedTemplateId(selectedTemplate.id);
    } catch (publishErr) {
      console.error(publishErr);
      setError(publishErr.message || 'Failed to publish template');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateTypeChange = async (nextType) => {
    if (!selectedTemplate || selectedTemplate.template_type === nextType) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateFormsTemplate(selectedTemplate.id, {
        schema: {
          ...(selectedTemplate.schema || {}),
          template_type: nextType
        }
      });
      await loadTemplates();
      setSelectedTemplateId(selectedTemplate.id);
    } catch (typeErr) {
      console.error(typeErr);
      setError(typeErr.message || 'Failed to update template type');
    } finally {
      setSaving(false);
    }
  };

  const renderTemplateDetail = () => {
    if (!selectedTemplate) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select a template to view details.
          </Typography>
        </Paper>
      );
    }

    const status = selectedTemplate.status;
    const fields = selectedTemplate.schema?.fields || [];
    const attachments = selectedTemplate.schema?.attachments || [];

    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedTemplate.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={status.toUpperCase()}
                    color={statusColorMap[status] || 'default'}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={selectedTemplate.template_type === 'pdf' ? 'PDF' : 'Builder'}
                  />
                  <Chip size="small" variant="outlined" label={`v${selectedTemplate.version}`} />
                </Stack>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => setPreviewOpen(true)}
              >
                Preview
              </Button>
            </Stack>
          </Box>

          {selectedTemplate.schema?.description && (
            <Typography variant="body2" color="text.secondary">
              {selectedTemplate.schema.description}
            </Typography>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<DesignServicesIcon />}
              variant="outlined"
              onClick={() => navigate(`/signing/templates/${selectedTemplate.id}/design`)}
            >
              Design Template
            </Button>
            {status !== 'published' && (
              <Button
                startIcon={<PublishIcon />}
                variant="contained"
                onClick={handlePublishTemplate}
                disabled={saving}
              >
                Publish
              </Button>
            )}
          </Stack>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Fields ({fields.length})
            </Typography>
            {fields.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No fields configured yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {fields.map((field) => (
                  <Paper key={field.id || field.field_key} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {field.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      key: {field.field_key} · type: {field.field_type}{' '}
                      {field.required ? '· required' : ''}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Attachments ({attachments.length})
            </Typography>
            {attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                PDF uploads are not yet supported in the new workflow.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {attachments.map((attachment) => (
                  <Paper key={attachment.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2">{attachment.file_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Stored at: {attachment.storage_path}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="section">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">Template Catalog</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTemplates}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            New Template
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7} lg={8} sx={{ order: { xs: 1, md: 0 } }}>
          {isSmallScreen ? (
            <Stack spacing={2}>
              {templates.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates yet. Create one to get started.
                  </Typography>
                </Paper>
              ) : (
                templates.map((template) => {
                  const isSelected = template.id === selectedTemplateId;
                  const typeLabel = template.template_type === 'pdf' ? 'PDF' : 'Builder';
                  return (
                    <Paper
                      key={template.id}
                      variant="outlined"
                      onClick={() => setSelectedTemplateId(template.id)}
                      sx={{
                        p: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {template.name}
                          </Typography>
                          {template.schema?.description && (
                            <Typography variant="caption" color="text.secondary">
                              {template.schema.description}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={template.status.toUpperCase()}
                            color={statusColorMap[template.status] || 'default'}
                          />
                          <Chip size="small" variant="outlined" label={typeLabel} />
                          <Chip size="small" variant="outlined" label={`v${template.version}`} />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${template.schema?.fields?.length ?? 0} fields`}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Updated{' '}
                          {new Date(
                            template.updated_at || template.updatedAt || Date.now()
                          ).toLocaleString()}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })
              )}
            </Stack>
          ) : (
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="30%">Name</TableCell>
                      <TableCell width="13%">Status</TableCell>
                      <TableCell width="13%">Type</TableCell>
                      <TableCell width="10%" align="center">
                        Version
                      </TableCell>
                      <TableCell width="12%" align="center">
                        Fields
                      </TableCell>
                      <TableCell width="22%">Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            No templates yet. Create one to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      templates.map((template) => {
                        const isSelected = template.id === selectedTemplateId;
                        const typeLabel =
                          template.template_type === 'pdf' ? 'PDF' : 'Builder';
                        return (
                          <TableRow
                            hover
                            key={template.id}
                            selected={isSelected}
                            onClick={() => setSelectedTemplateId(template.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {template.name}
                              </Typography>
                              {template.schema?.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {template.schema.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={template.status.toUpperCase()}
                                color={statusColorMap[template.status] || 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip size="small" variant="outlined" label={typeLabel} />
                            </TableCell>
                            <TableCell align="center">v{template.version}</TableCell>
                            <TableCell align="center">
                              {template.schema?.fields?.length ?? 0}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(
                                  template.updated_at || template.updatedAt || Date.now()
                                ).toLocaleString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>
        <Grid item xs={12} md={5} lg={4} sx={{ order: { xs: 2, md: 0 } }}>
          {renderTemplateDetail()}
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Template</DialogTitle>
        <form onSubmit={handleCreateTemplate}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Template Name"
                value={formState.name}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, name: event.target.value }))
                }
                required
              />
              <TextField
                label="Description"
                multiline
                minRows={3}
                value={formState.description}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, description: event.target.value }))
                }
              />
              <TextField
                select
                label="Template Type"
                value={formState.template_type}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, template_type: event.target.value }))
                }
              >
                <MenuItem value="builder">Builder (dynamic form)</MenuItem>
                <MenuItem value="pdf" disabled>
                  PDF upload (coming soon)
                </MenuItem>
              </TextField>
              <Typography variant="body2" color="text.secondary">
                A starter acknowledgment checkbox and signature block will be added automatically.
                You can refine the layout in the designer.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Creating…' : 'Create Template'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <TemplatePreview open={previewOpen} onClose={() => setPreviewOpen(false)} template={selectedTemplate} />
    </Box>
  );
};

export default TemplatePanel;
